import { forwardRef, useCallback, useId, useImperativeHandle, useMemo, useRef } from "react";
import { createFormHook } from "@tanstack/react-form";
import { isObject } from "lodash";
import type { FormAPI, FormAdapterProps } from "../types";
import { FieldRefsContext, useFieldRefsAPI } from "./FieldRefsContext";
import { FormAPIContext, fieldContext, formContext } from "./contexts";
import { ValidatorProvider } from "./validator";

export const { useAppForm } = createFormHook({
    fieldComponents: {},
    formComponents: {},
    fieldContext,
    formContext,
});

export const FormAdapter = forwardRef<FormAPI, FormAdapterProps>((props, ref) => {
    const { initialValues, onSubmit, children, className, ...htmlAttrs } = props;

    const formId = useId();

    /**
     * The baseline is the values a reset returns to, and the values the form library
     * compares against. It is captured from `initialValues` on mount and moved only by
     * `reset`; later changes to the prop are ignored.
     *
     * This is what keeps the library's own re-sync dormant. On every render of this
     * component the library compares the `defaultValues` it is handed against its
     * stored copy, and replaces all form values with the prop if they differ and no
     * field has been touched. `reset(values)` writes the library's stored copy and
     * clears touched state, so without this the next render from an ancestor would
     * silently roll the form back to the mount-time values.
     *
     * A ref rather than state: nothing renders from the baseline, so moving it must not
     * cost a render. The library reads it during the layout effect of whatever render
     * comes next, which is exactly when the comparison happens.
     *
     * See ADR-016 for the library mechanism and the alternatives considered.
     */
    const baselineRef = useRef(initialValues);

    const form = useAppForm({
        defaultValues: baselineRef.current,
        onSubmit: async ({ value, meta }) => {
            await onSubmit(
                value,
                // TS struggles to infer generic types in components wrapped in forwardRef
                // So we resort to type assertion here
                meta as Parameters<typeof onSubmit>[1],
            );
        },
    });

    /**
     * Moves values and baseline together, so no render observes them apart: `form.reset`
     * writes the library's stored copy synchronously, and the next render hands it back
     * the same object.
     */
    const reset = useCallback<FormAPI["reset"]>(
        (values) => {
            if (values) {
                baselineRef.current = values;
            }
            form.reset(values);
        },
        [form],
    );

    const fieldRefsContextValue = useFieldRefsAPI();

    const setFocus = useCallback(
        (path: string) => {
            const element = fieldRefsContextValue.getFieldRef(path);

            if (isObject(element) && "focus" in element && typeof element.focus === "function") {
                element.focus();
            }
        },
        [fieldRefsContextValue], // dependencies are guaranteed to never change
    );

    const formAPI = useMemo<FormAPI>(
        () => ({
            reset,

            getFieldValue: (path) => form.getFieldValue(path) as any,
            getValues: () => form.state.values,
            getDefaultValues: () => form.options.defaultValues as any,
            setFieldValue: (path, value) => form.setFieldValue(path, value),
            setValues: (values) => {
                // TanStack Form does not expose a bulk setValues/patchValue API.
                // Iterating over setFieldValue is the de facto supported workaround.
                for (const [key, value] of Object.entries(values)) {
                    form.setFieldValue(key, value);
                }
            },
            appendArrayItem: (path, value) => {
                const currentArray = form.getFieldValue(path) as unknown[];
                const index = Array.isArray(currentArray) ? currentArray.length : 0;
                form.insertFieldValue(path, index, value);
            },
            removeArrayItem: (path, index) => form.removeFieldValue(path, index),
            handleSubmit: form.handleSubmit,
            setFocus,
        }),
        [form, reset, setFocus], // dependencies are guaranteed to never change
    );

    useImperativeHandle(ref, () => formAPI);

    return (
        <form
            id={formId}
            onSubmit={(e) => {
                e.stopPropagation();
                e.preventDefault();
                form.handleSubmit();
            }}
            className={className}
            {...htmlAttrs}
        >
            <form.AppForm>
                <FormAPIContext.Provider value={formAPI}>
                    <FieldRefsContext.Provider value={fieldRefsContextValue}>
                        <ValidatorProvider formId={formId}>{children}</ValidatorProvider>
                    </FieldRefsContext.Provider>
                </FormAPIContext.Provider>
            </form.AppForm>
        </form>
    );
});

FormAdapter.displayName = "FormAdapter";
